import { getUsersPreferencesUnitsQueryKey } from "@imi/api-client/index.js";
import { UndoIcon } from "@imi/icons/UndoIcon.js";
import { Button } from "@imi/ui/components/Button/Button.js";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogClose,
  DialogCloseButton,
  DialogHeader,
} from "@imi/ui/components/Dialog/Dialog.js";
import { Heading } from "@imi/ui/components/Heading/Heading.js";
import { unitsRepository } from "@src/core/engine/units/unitsRepository";
import { useAnalytics } from "@src/shared/components/Analytics/useAnalytics";
import { selectSubmitOnEnter } from "@src/shared/helpers/form";
import { useQueryClient } from "@tanstack/react-query";
import { Suspense, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { Form } from "react-router";
import { DialogErrorFallback } from "../../components/DialogErrorFallback";
import { DialogLoadingFallback } from "../../components/DialogLoadingFallback";
import { useRouteDialogProps } from "../../hooks/useRouteDialogProps";
import { FluidProperties } from "./components/FluidProperties";
import { GeometricDimensions } from "./components/GeometricDimensions";
import { MassAndForce } from "./components/MassAndForce";
import { Power } from "./components/Power";
import { PressureAndFlow } from "./components/PressureAndFlow";
import { ThermalProperties } from "./components/ThermalProperties";
import { useUnitsForm } from "./hooks/useUnitsForm";
import styles from "./SettingsUnitsEditDialog.module.css";

export function SettingsUnitsEditDialog() {
  const { t } = useTranslation("settings", { keyPrefix: "units" });
  const routeDialogProps = useRouteDialogProps();
  const { tracker } = useAnalytics();

  return (
    <Dialog {...routeDialogProps}>
      <DialogHeader>
        <Heading as="h3" subTitle={t("subTitle")}>
          {t("title")}
        </Heading>
      </DialogHeader>
      <ErrorBoundary FallbackComponent={DialogErrorFallback}>
        <Suspense fallback={<DialogLoadingFallback />}>
          <SettingsUnitsEditDialogContent />
        </Suspense>
      </ErrorBoundary>
      <DialogCloseButton
        onClick={() => {
          tracker?.trackEvent("settings-unit-closed");
        }}
      >
        {t("actions.close")}
      </DialogCloseButton>
    </Dialog>
  );
}

function SettingsUnitsEditDialogContent() {
  const { t } = useTranslation("settings", { keyPrefix: "units" });
  const routeDialogProps = useRouteDialogProps();
  const queryClient = useQueryClient();
  const unitsOptions = useMemo(() => unitsRepository.getOptions(), []);
  const { tracker } = useAnalytics();
  const { defaultValue, form, submitting } = useUnitsForm({
    onSubmit: () => {
      queryClient.invalidateQueries({
        queryKey: getUsersPreferencesUnitsQueryKey(),
      });
      tracker?.trackEvent("settings-unit-saved");
      routeDialogProps.onOpenChange(false);
    },
  });

  return (
    <>
      <DialogBody className={styles.DialogBody}>
        <Form
          className={styles.Form}
          id={form.formId}
          onKeyDown={(e) => selectSubmitOnEnter(e, submitting)}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit(e);
          }}
        >
          <PressureAndFlow form={form} unitsOptions={unitsOptions} />
          <GeometricDimensions form={form} unitsOptions={unitsOptions} />
          <ThermalProperties form={form} unitsOptions={unitsOptions} />
          <FluidProperties form={form} unitsOptions={unitsOptions} />
          <MassAndForce form={form} unitsOptions={unitsOptions} />
          <Power form={form} unitsOptions={unitsOptions} />
        </Form>
      </DialogBody>
      <DialogActions className={styles.DialogActions}>
        <Button
          className={styles.ResetButton}
          disabled={submitting}
          onClick={() => {
            form.reset(defaultValue);
            tracker?.trackEvent("settings-unit-reset");
          }}
          variant="outline"
        >
          <UndoIcon />
          {t("actions.reset")}
        </Button>
        <DialogClose
          disabled={submitting}
          onClick={() => {
            tracker?.trackEvent("settings-unit-cancelled");
          }}
          variant="outline"
        >
          {t("actions.cancel")}
        </DialogClose>
        <Button form={form.formId} loading={submitting} type="submit">
          {t("actions.confirm")}
        </Button>
      </DialogActions>
    </>
  );
}
